import React, { useEffect, useState } from 'react';
import { TrendingUpIcon, CreditCardIcon, EditIcon, SaveIcon, XIcon, InfoIcon, SparklesIcon } from 'lucide-react';
// Generar últimos 12 meses desde diciembre 2024
const generateLast12Months = () => {
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const result = [];
  const startDate = new Date(2024, 11, 1); // Diciembre 2024
  for (let i = 0; i < 12; i++) {
    const date = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    result.push({
      month: months[monthIndex],
      year: year,
      credits: 0,
      uf: 0
    });
  }
  return result;
};
const CREDIT_GOAL = 500;
const UF_GOAL = 1500000;
// Frases motivacionales de grandes emprendedores
const motivationalQuotes = [{
  quote: 'Tu tiempo es limitado, no lo desperdicies viviendo la vida de alguien más.',
  author: 'Steve Jobs'
}, {
  quote: 'La innovación distingue a un líder de un seguidor.',
  author: 'Steve Jobs'
}, {
  quote: 'El éxito es un mal profesor. Seduce a la gente inteligente a pensar que no pueden perder.',
  author: 'Bill Gates'
}, {
  quote: 'Si naces pobre, no es tu culpa. Pero si mueres pobre, sí lo es.',
  author: 'Bill Gates'
}, {
  quote: 'No tengas miedo de renunciar a lo bueno para ir a por lo grandioso.',
  author: 'John D. Rockefeller'
}, {
  quote: 'El fracaso es simplemente la oportunidad de comenzar de nuevo, esta vez de forma más inteligente.',
  author: 'Henry Ford'
}, {
  quote: 'La calidad significa hacer las cosas bien cuando nadie está mirando.',
  author: 'Henry Ford'
}, {
  quote: 'Tanto si piensas que puedes, como si piensas que no puedes, estás en lo cierto.',
  author: 'Henry Ford'
}, {
  quote: 'El emprendedor siempre busca el cambio, responde a él y lo aprovecha como una oportunidad.',
  author: 'Peter Drucker'
}, {
  quote: 'Las oportunidades no ocurren. Las creas tú.',
  author: 'Chris Grosser'
}, {
  quote: 'El único modo de hacer un gran trabajo es amar lo que haces.',
  author: 'Steve Jobs'
}, {
  quote: 'No te preocupes por el fracaso. Preocúpate por las oportunidades que pierdes cuando ni siquiera lo intentas.',
  author: 'Jack Canfield'
}, {
  quote: 'El éxito no es la clave de la felicidad. La felicidad es la clave del éxito.',
  author: 'Albert Schweitzer'
}, {
  quote: 'La mejor manera de predecir el futuro es crearlo.',
  author: 'Peter Drucker'
}, {
  quote: 'No esperes. Nunca habrá un momento perfecto.',
  author: 'Napoleon Hill'
}];
export function MetaPage() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [ufValue, setUfValue] = useState<number | null>(null);
  const [usdValue, setUsdValue] = useState<number | null>(null);
  const [monthlyData, setMonthlyData] = useState(generateLast12Months());
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(monthlyData);
  const [showMotivation, setShowMotivation] = useState(false);
  const [dailyQuote, setDailyQuote] = useState(motivationalQuotes[0]);
  const totalCredits = monthlyData.reduce((sum, m) => sum + m.credits, 0);
  const totalUF = monthlyData.reduce((sum, m) => sum + m.uf, 0);
  // Calcular el máximo valor para escalar ambos gráficos proporcionalmente
  const maxCredits = Math.max(...monthlyData.map(m => m.credits), CREDIT_GOAL);
  const maxUF = Math.max(...monthlyData.map(m => m.uf), UF_GOAL);
  // Escala común: ambas metas deben estar a la misma altura visual (50%)
  const creditScale = maxCredits * 2; // Meta al 50%
  const ufScale = maxUF * 2; // Meta al 50%
  // Seleccionar frase del día (basada en la fecha para que sea consistente durante el día)
  useEffect(() => {
    const updateDailyQuote = () => {
      const today = new Date();
      const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000);
      const quoteIndex = dayOfYear % motivationalQuotes.length;
      setDailyQuote(motivationalQuotes[quoteIndex]);
    };
    updateDailyQuote();
    // Actualizar la frase a las 12:34 cada día
    const checkForQuoteUpdate = () => {
      const now = new Date();
      if (now.getHours() === 12 && now.getMinutes() === 34 && now.getSeconds() === 0) {
        updateDailyQuote();
      }
    };
    const interval = setInterval(checkForQuoteUpdate, 1000);
    return () => clearInterval(interval);
  }, []);
  // Detectar las 12:34 y mostrar mensaje motivacional por 1 minuto
  useEffect(() => {
    const checkTime = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      // Si son las 12:34
      if (hours === 12 && minutes === 34) {
        setShowMotivation(true);
        // Ocultar después de 1 minuto
        setTimeout(() => {
          setShowMotivation(false);
        }, 60000); // 60 segundos
      }
    };
    // Verificar cada segundo
    const interval = setInterval(checkTime, 1000);
    checkTime(); // Verificar inmediatamente al cargar
    return () => clearInterval(interval);
  }, []);
  // Fetch UF y USD del Banco Central - una vez al día
  useEffect(() => {
    const fetchIndicators = async () => {
      try {
        // Fetch UF
        const ufResponse = await fetch('https://mindicador.cl/api/uf');
        const ufData = await ufResponse.json();
        if (ufData?.serie?.[0]?.valor) {
          setUfValue(ufData.serie[0].valor);
        }
        // Fetch USD
        const usdResponse = await fetch('https://mindicador.cl/api/dolar');
        const usdData = await usdResponse.json();
        if (usdData?.serie?.[0]?.valor) {
          setUsdValue(usdData.serie[0].valor);
        }
      } catch (error) {
        console.error('Error fetching indicators:', error);
        setUfValue(39000);
        setUsdValue(950);
      }
    };
    // Fetch inicial
    fetchIndicators();
    // Actualizar una vez al día (24 horas)
    const interval = setInterval(fetchIndicators, 86400000); // 24 horas en ms
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const formatCLP = (valor: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(valor);
  };
  const handleEdit = () => {
    setEditData([...monthlyData]);
    setIsEditing(true);
  };
  const handleSave = () => {
    setMonthlyData(editData);
    setIsEditing(false);
  };
  const handleCancel = () => {
    setEditData(monthlyData);
    setIsEditing(false);
  };
  const handleDataChange = (index: number, field: 'credits' | 'uf', value: string) => {
    const newData = [...editData];
    newData[index][field] = parseInt(value) || 0;
    setEditData(newData);
  };
  // Pantalla motivacional a las 12:34
  if (showMotivation) {
    return <div className="fixed inset-0 bg-black z-[9999] flex items-center justify-center p-8 animate-fade-in" data-id="element-359">
        <div className="text-center max-w-4xl" data-id="element-360">
          <div className="mb-12 animate-pulse-slow" data-id="element-361">
            <p className="text-9xl md:text-[12rem] font-bold text-white mb-4" data-id="element-362">
              12:34
            </p>
            <p className="text-2xl md:text-3xl text-white/60 font-light tracking-wider" data-id="element-363">
              Un momento para reflexionar
            </p>
          </div>

          <div className="space-y-8 animate-fade-in-up" data-id="element-364">
            <p className="text-2xl md:text-4xl text-white font-light leading-relaxed italic" data-id="element-365">
              "{dailyQuote.quote}"
            </p>
            <p className="text-xl md:text-2xl text-white/80 font-medium" data-id="element-366">
              — {dailyQuote.author}
            </p>
          </div>

          <div className="mt-16" data-id="element-367">
            <div className="inline-flex items-center gap-2 text-white/40 text-sm" data-id="element-368">
              <CreditCardIcon className="h-5 w-5" data-id="element-369" />
              <span className="font-semibold" data-id="element-370">Creditual</span>
            </div>
          </div>
        </div>

        <style data-id="element-371">{`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes fade-in-up {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse-slow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.8; }
          }
          .animate-fade-in {
            animation: fade-in 1s ease-out;
          }
          .animate-fade-in-up {
            animation: fade-in-up 1.5s ease-out 0.5s both;
          }
          .animate-pulse-slow {
            animation: pulse-slow 3s ease-in-out infinite;
          }
        `}</style>
      </div>;
  }
  return <div className="h-screen bg-white overflow-hidden flex flex-col" data-id="element-372">
      {/* Header compacto */}
      <header className="border-b border-gray-200 bg-white/95 backdrop-blur flex-shrink-0" data-id="element-373">
        <div className="container mx-auto max-w-7xl px-4 py-2" data-id="element-374">
          <div className="flex items-center justify-between" data-id="element-375">
            {/* Logo - ahora con link al home */}
            <a href="/" aria-label="Ir al inicio" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-id="element-376">
              <CreditCardIcon className="h-6 w-6 text-[#2B5FD9]" data-id="element-377" />
              <div data-id="element-378">
                <h1 className="text-lg font-bold text-[#0A1628]" data-id="element-379">Creditual</h1>
                <p className="text-[10px] text-[#6B7280]" data-id="element-380">
                  Dashboard de Métricas
                </p>
              </div>
            </a>

            {/* Info contextual compacta */}
            <div className="flex items-center gap-6" data-id="element-381">
              <div className="text-right" data-id="element-382">
                <p className="text-[10px] text-[#6B7280]" data-id="element-383">Hoy</p>
                <p className="text-xs font-semibold text-[#0A1628]" data-id="element-384">
                  {currentTime.toLocaleDateString('es-CL', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                }).replace(/^\w/, c => c.toUpperCase())}
                </p>
              </div>
              {ufValue && <div className="text-right" data-id="element-385">
                  <p className="text-[10px] text-[#6B7280]" data-id="element-386">UF</p>
                  <p className="text-xs font-semibold text-[#0A1628]" data-id="element-387">
                    {formatCLP(ufValue)}
                  </p>
                </div>}
              {usdValue && <div className="text-right" data-id="element-388">
                  <p className="text-[10px] text-[#6B7280]" data-id="element-389">USD</p>
                  <p className="text-xs font-semibold text-[#0A1628]" data-id="element-390">
                    {formatCLP(usdValue)}
                  </p>
                </div>}
              <div className="text-right" data-id="element-391">
                <p className="text-2xl font-bold text-[#2B5FD9]" data-id="element-392">
                  {currentTime.toLocaleTimeString('es-CL', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Contenido principal - optimizado para una pantalla */}
      <div className="flex-1 overflow-hidden" data-id="element-393">
        <div className="container mx-auto max-w-7xl px-4 py-4 h-full flex flex-col" data-id="element-394">
          {/* Frase del día - compacta */}
          <div className="mb-4 relative overflow-hidden bg-gradient-to-br from-[#2B5FD9] to-[#1E4BB8] rounded-xl p-4 shadow-lg flex-shrink-0" data-id="element-395">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" data-id="element-396" />
            <div className="relative flex items-center gap-3" data-id="element-397">
              <SparklesIcon className="h-4 w-4 text-white/80 flex-shrink-0" data-id="element-398" />
              <div className="flex-1 min-w-0" data-id="element-399">
                <p className="text-sm text-white font-light leading-relaxed italic truncate" data-id="element-400">
                  "{dailyQuote.quote}"
                </p>
                <p className="text-xs text-white/80 font-medium mt-1" data-id="element-401">
                  — {dailyQuote.author}
                </p>
              </div>
            </div>
          </div>

          {/* Grid principal - 2 columnas: gráficos arriba, métricas abajo */}
          <div className="flex-1 grid grid-rows-[1fr_auto] gap-4 min-h-0" data-id="element-402">
            {/* Gráficos principales */}
            <div className="grid grid-cols-2 gap-4 min-h-0" data-id="element-403">
              {/* Gráfico 1: Créditos Colocados */}
              <div className="relative overflow-hidden bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-404">
                <div className="flex items-center justify-between mb-3" data-id="element-405">
                  <div data-id="element-406">
                    <h2 className="text-base font-bold text-[#0A1628]" data-id="element-407">
                      Créditos Colocados
                    </h2>
                    <p className="text-[10px] text-[#6B7280]" data-id="element-408">Desde dic 2024</p>
                  </div>
                  <div className="text-right" data-id="element-409">
                    <p className="text-[10px] text-[#6B7280]" data-id="element-410">Meta</p>
                    <div className="flex items-center gap-1" data-id="element-411">
                      <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-412"></div>
                      <p className="text-sm font-bold text-[#0A1628]" data-id="element-413">
                        {CREDIT_GOAL}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative h-[calc(100%-4rem)] flex items-end justify-between gap-1" data-id="element-414">
                  <div className="absolute left-0 right-0 border-t border-dashed border-red-500 z-10" style={{
                  bottom: '50%'
                }} data-id="element-415" />

                  {monthlyData.map((data, index) => {
                  const height = Math.max(data.credits / creditScale * 100, 2);
                  const isAboveGoal = data.credits >= CREDIT_GOAL;
                  return <div key={index} className="flex-1 flex flex-col items-center group" data-id="element-416">
                        <div className="w-full relative h-full" data-id="element-417">
                          <div className={`absolute bottom-0 w-full rounded-t transition-all duration-300 ${data.credits === 0 ? 'bg-gray-200' : isAboveGoal ? 'bg-gradient-to-t from-green-600 to-green-400' : 'bg-gradient-to-t from-[#2B5FD9] to-[#4A7FE8]'}`} style={{
                        height: `${height}%`
                      }} data-id="element-418">
                            {data.credits > 0 && <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-419">
                                <div className="bg-[#0A1628] text-white text-[10px] font-bold px-1 py-0.5 rounded whitespace-nowrap" data-id="element-420">
                                  {data.credits}
                                </div>
                              </div>}
                          </div>
                        </div>
                        <p className="text-[10px] font-medium mt-1 text-[#6B7280]" data-id="element-421">
                          {data.month}
                        </p>
                      </div>;
                })}
                </div>
              </div>

              {/* Gráfico 2: UF Colocadas */}
              <div className="relative overflow-hidden bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-422">
                <div className="flex items-center justify-between mb-3" data-id="element-423">
                  <div data-id="element-424">
                    <h2 className="text-base font-bold text-[#0A1628]" data-id="element-425">
                      UF Colocadas
                    </h2>
                    <p className="text-[10px] text-[#6B7280]" data-id="element-426">Desde dic 2024</p>
                  </div>
                  <div className="text-right" data-id="element-427">
                    <p className="text-[10px] text-[#6B7280]" data-id="element-428">Meta</p>
                    <div className="flex items-center gap-1" data-id="element-429">
                      <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-430"></div>
                      <p className="text-sm font-bold text-[#0A1628]" data-id="element-431">
                        {(UF_GOAL / 1000000).toFixed(1)}M
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative h-[calc(100%-4rem)] flex items-end justify-between gap-1" data-id="element-432">
                  <div className="absolute left-0 right-0 border-t border-dashed border-red-500 z-10" style={{
                  bottom: '50%'
                }} data-id="element-433" />

                  {monthlyData.map((data, index) => {
                  const height = Math.max(data.uf / ufScale * 100, 2);
                  const isAboveGoal = data.uf >= UF_GOAL;
                  return <div key={index} className="flex-1 flex flex-col items-center group" data-id="element-434">
                        <div className="w-full relative h-full" data-id="element-435">
                          <div className={`absolute bottom-0 w-full rounded-t transition-all duration-300 ${data.uf === 0 ? 'bg-gray-200' : isAboveGoal ? 'bg-gradient-to-t from-green-600 to-green-400' : 'bg-gradient-to-t from-[#2B5FD9] to-[#4A7FE8]'}`} style={{
                        height: `${height}%`
                      }} data-id="element-436">
                            {data.uf > 0 && <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-437">
                                <div className="bg-[#0A1628] text-white text-[10px] font-bold px-1 py-0.5 rounded whitespace-nowrap" data-id="element-438">
                                  {(data.uf / 1000).toFixed(0)}K
                                </div>
                              </div>}
                          </div>
                        </div>
                        <p className="text-[10px] font-medium mt-1 text-[#6B7280]" data-id="element-439">
                          {data.month}
                        </p>
                      </div>;
                })}
                </div>
              </div>
            </div>

            {/* Métricas históricas + footer */}
            <div className="grid grid-cols-2 gap-4" data-id="element-440">
              {/* Total Créditos */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-441">
                <div className="flex items-center gap-3" data-id="element-442">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2B5FD9] to-[#1E4BB8] p-0.5 shadow-md flex-shrink-0" data-id="element-443">
                    <div className="w-full h-full bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-444">
                      <TrendingUpIcon className="h-5 w-5 text-[#2B5FD9]" data-id="element-445" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-446">
                    <p className="text-[10px] text-[#6B7280]" data-id="element-447">
                      Total Histórico
                    </p>
                    <h3 className="text-2xl font-bold text-[#0A1628]" data-id="element-448">
                      {totalCredits.toLocaleString('es-CL')}
                    </h3>
                    <p className="text-xs text-[#6B7280]" data-id="element-449">Créditos colocados</p>
                  </div>
                </div>
              </div>

              {/* Total UF */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-450">
                <div className="flex items-center gap-3" data-id="element-451">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-600 to-green-500 p-0.5 shadow-md flex-shrink-0" data-id="element-452">
                    <div className="w-full h-full bg-green-50 rounded-lg flex items-center justify-center" data-id="element-453">
                      <CreditCardIcon className="h-5 w-5 text-green-600" data-id="element-454" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-455">
                    <p className="text-[10px] text-[#6B7280]" data-id="element-456">
                      Total Histórico
                    </p>
                    <h3 className="text-2xl font-bold text-[#0A1628]" data-id="element-457">
                      {totalUF === 0 ? '0' : (totalUF / 1000000).toFixed(1) + 'M'}
                    </h3>
                    <p className="text-xs text-[#6B7280]" data-id="element-458">UF colocadas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer discreto */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 flex-shrink-0" data-id="element-459">
            <p className="text-[10px] text-[#6B7280]" data-id="element-460">
              <span className="font-medium" data-id="element-461">Crédito colocado:</span> Escritura
              firmada por comprador y vendedor
            </p>
            {!isEditing && <button onClick={handleEdit} className="flex items-center gap-1 px-2 py-1 bg-[#2B5FD9] text-white text-[10px] font-medium rounded hover:bg-[#1E4BB8] transition-colors" data-id="element-462">
                <EditIcon className="h-3 w-3" data-id="element-463" />
                Editar
              </button>}
          </div>
        </div>
      </div>
    </div>;
}
export default MetaPage;